import { useState } from 'react';
import { TextField, Autocomplete } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { Controller } from 'react-hook-form';
import { authHeader } from '../../store/auth-slice/auth-slice';
import { axiosInstance } from '../../axios/axiosHandler';
import { useTranslation } from 'react-i18next';

interface SelectInputProps {
  url: string;
  name: string;
  label?: string;
  labelName: string;
  idName: string;
  multiple?: boolean;
  disabled?: boolean;
  control: any;
  translateName?: boolean,
  sx?: any;
}

function SelectInput({
  url,
  name,
  label,
  labelName,
  idName,
  multiple,
  disabled,
  control,
  translateName,
  sx,
  ...other
}: SelectInputProps) {
    const { t } = useTranslation();
    const [options, setOptions] = useState([]);
    const [optionsLoading, setOptionsLoading] = useState(false);

    const fetchOptions = async () => {
        if (options.length === 0) {
            setOptionsLoading(true);
            try {
                const { data } = await axiosInstance.get(url, authHeader());
                if(translateName) {
                    setOptions(data.map((el : any) => { 
                        return {...el, [labelName]: t(`translations:${el[labelName]}`)}
                    }));
                }
                else {
                    setOptions(data);
                }
                setOptionsLoading(false);
            } catch (error) {
                return;
            }
        }
    };

    return (
        <Controller
        render={({
            field: { onBlur, value, onChange, ref },
            fieldState: { invalid, error },
        }) => (
            <Autocomplete
                sx={sx ?? null}
                multiple={multiple}
                fullWidth
                disabled={disabled || false}
                onOpen={() => fetchOptions()}
                options={options || []}
                loading={optionsLoading}
                isOptionEqualToValue={(option: any, value: any) =>
                    option[idName] === value[idName]
                }
                getOptionLabel={(option: any) => option[labelName] || ''}
                filterSelectedOptions={true}
                value={value || []}
                onChange={(e, values) => onChange(values)}
                renderOption={(props, option) => (
                    <li {...props} key={option[idName]}>
                        {option[labelName]}
                    </li>
                )}
                renderInput={(params : any) => (
                    <TextField
                        {...params}
                        ref={ref}
                        onBlur={onBlur}
                        error={invalid}
                        label={label}
                        helperText={error?.message}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                            <>
                                {optionsLoading ? (<CircularProgress color="inherit" size={20} />) : null}
                                {params.InputProps.endAdornment}
                            </>
                            ),
                        }}
                    />
                )}
                {...other}
            />
        )}
        name={name}
        control={control}
        />
    );
}

export default SelectInput;
